/**
 * Vue Router
 *
 * @library
 *
 * https://router.vuejs.org/en/
 */

// Lib imports
import Vue from 'vue';
import Router from 'vue-router';
import Meta from 'vue-meta';
import store from '../store';

// Routes
import paths, { shopifySections } from './paths';
import { Routes } from '@/helpers/consts';

function route({ view, name, props, children = [], ...routeData }) {
  return {
    name: view === 'TabView' ? name : name || view?.split('/').pop() || '',
    component: resolve => import(`@/views/${view.split('_')[0]}.vue`).then(resolve),
    children: children.map(path => route({ ...path, path: path.path.replace(/^\//, '') })),
    props,
    ...routeData,
  };
}

Vue.use(Router);

const routes = paths.map(path => route(path));
// Create a new router
const router = new Router({
  base: '/dashboard',
  mode: 'history',
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    }
    if (to.hash && from.name !== 'Auth') {
      return { selector: to.hash };
    }
    return { x: 0, y: 0 };
  },
});

router.beforeEach((to, from, next) => {
  const { isFormChanged, tempHistory, manualHistory } = store.state.app;

  if (isFormChanged) {
    store.commit('app/setNextRoute', to);
    return;
  }
  store.commit('app/setNextRoute', null);

  const isLocal = from.matched[0]?.path === to.matched[0]?.path;

  const rootName =
    to.meta.parent || (isLocal ? from.name : to.matched.filter(el => el.name)[0]?.name) || to.name;

  store.commit('app/setRootNavigation', rootName);

  if (tempHistory !== null) {
    store.commit('app/commitManualHistory', rootName);
  }

  if (
    manualHistory.length &&
    manualHistory[manualHistory.length - 1].name === to.name &&
    from.matched.map(e => e.path).includes(to.path)
  ) {
    store.commit('app/setManualHistory', []);
  }

  window.document.body.dispatchEvent(
    new CustomEvent('saveform-changed', {
      detail: null,
      composed: true,
      bubbles: true,
    })
  );

  const { auth } = Routes;
  const queryParams = to.fullPath.split('?')[1] || '';

  if (to.name === 'Welcome') {
    document.body.classList.add('opinew-onboarding');
  } else {
    document.body.classList.remove('opinew-onboarding');
  }

  if (from.path.split('/')[1] && from.path.split('/')[1] !== to.path.split('/')[1]) {
    store.commit('updateLoadingCount', 1);
    setTimeout(() => {
      store.commit('updateLoadingCount', -1);
    }, 100);
  }

  if (to.path === auth) {
    sessionStorage.removeItem('vuex');
    next();
  } else if (
    store.state.accountInfo.shopNeedsToChoosePlan &&
    !['TrialEnded', 'Account', 'PlanPick'].includes(to.name) &&
    !queryParams.includes('subscription_activated=true')
  ) {
    next({ name: 'TrialEnded', hash: to.hash });
  } else if (to.name !== 'Welcome' && queryParams.includes('welcome_screen=')) {
    const welcome_screen_step = new URLSearchParams(window.location.search).get('welcome_screen');
    next({
      name: 'Welcome',
      params: { step: welcome_screen_step },
      hash: to.hash,
    });
  } else {
    store.dispatch('hideSaverbar');

    const nextSectionIndex = shopifySections.findIndex(el => el.path === to.matched[0].path);

    if (window.opwBridge) {
      window.opwBridge.setActive(nextSectionIndex);
    }
    if (window.OPW_NAVIGATION) {
      const activeMenuItem = window.OPW_NAVIGATION.items.find(el =>
        to.path.includes(el.destination.path.replace('/dashboard', ''))
      );
      window.OPW_NAVIGATION.set({ active: activeMenuItem || undefined });
    }

    next();
  }
});

Vue.use(Meta);

export default router;
